import React, { useState, useEffect } from 'react';
import './Settings.css';
import {
  FaArrowLeft,
  FaUserCircle,
  FaBell,
  FaPalette,
  FaShieldAlt,
  FaGlobe,
  FaMoon,
  FaSun,
  FaHistory,
  FaTrash,
  FaComments,
  FaDownload,
  FaUndo,
  FaRobot,
  FaTicketAlt
} from 'react-icons/fa';


const ASSISTANTS = ['QMS', 'HSE', 'PO', 'TEA', 'ASK HR'];

const Settings = ({
  onClose,
  theme,
  onThemeChange,
  settings,
  onSettingsChange,
  selectedChatMode,
  onChangeChatMode,
}) => {
  const [activeTab, setActiveTab] = useState('chat');

  // Parse profile from localStorage SSO display name
  const rawDisplayName = localStorage.getItem('displayName') || '';
  const parseProfile = (dn) => {
    if (!dn) return { name: 'User', dept: '', email: '' };
    const namePart = dn.split('(')[0].trim();
    let name = namePart;
    if (namePart.includes(',')) {
      const [last, first] = namePart.split(',').map(s => s.trim());
      name = `${first} ${last}`;
    }
    const deptMatch = dn.match(/\(([^)]+)\)/);
    const dept = deptMatch ? deptMatch[1] : '';
    const emailName = name.toLowerCase().replace(/\s+/g, '.');
    return { name, dept, email: emailName ? `${emailName}@nlng.com` : '' };
  };
  const profile = parseProfile(rawDisplayName);

  // Apply font size to CSS variable live
  useEffect(() => {
    if (settings?.fontSize) {
      document.documentElement.style.setProperty('--chat-font-size', `${settings.fontSize}px`);
    }
  }, [settings?.fontSize]);

  const update = (key, value) => {
    if (onSettingsChange) onSettingsChange(key, value);
  };

  // Reusable toggle row â€” defaults to ON unless setting is explicitly false
  const ToggleItem = ({ settingKey, label, description, defaultOn = true }) => (
    <div className="toggle-item">
      <div className="toggle-info">
        <h5>{label}</h5>
        {description && <p>{description}</p>}
      </div>
      <label className="toggle-switch">
        <input
          type="checkbox"
          checked={settings?.[settingKey] !== undefined ? !!settings[settingKey] : defaultOn}
          onChange={(e) => update(settingKey, e.target.checked)}
        />
        <span className="toggle-slider"></span>
      </label>
    </div>
  );

  const tabs = [
    { id: 'chat',       label: 'Chat Preferences',  icon: <FaComments /> },
    { id: 'profile',    label: 'Profile',            icon: <FaUserCircle /> },
    { id: 'appearance', label: 'Appearance',         icon: <FaPalette /> },
    { id: 'privacy',    label: 'Privacy & Security', icon: <FaShieldAlt /> },
    { id: 'about',      label: 'About',              icon: <FaGlobe /> },
  ];

  const renderContent = () => {
    switch (activeTab) {

      /* â”€â”€ CHAT PREFERENCES â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
      case 'chat':
        return (
          <div className="settings-content">
            <h3 className="content-title">Chat Preferences</h3>

            <div className="settings-section-label">Assistant</div>
            <div className="form-group">
              <label>Default Assistant</label>
              <select
                value={settings?.defaultAssistant || selectedChatMode || 'QMS AI'}
                onChange={(e) => {
                  update('defaultAssistant', e.target.value);
                  if (onChangeChatMode) onChangeChatMode(e.target.value);
                }}
              >
                {ASSISTANTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div className="settings-section-label" style={{ marginTop: '1.5rem' }}>Behaviour</div>
            <div className="toggle-group">
              <ToggleItem
                settingKey="enableSuggestions"
                label="Further Questions / Suggestions"
                description="Show suggested follow-up questions after each AI response"
              />
              <ToggleItem
                settingKey="typingIndicators"
                label="Typing Indicators"
                description='"is thinkingâ€¦" animation while AI is generating a response'
              />
            </div>

            <div className="settings-section-label" style={{ marginTop: '1.5rem' }}>Display</div>
            <div className="form-group">
              <label>
                Font Size&nbsp;
                <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>
                  {settings?.fontSize || 16}px
                </span>
              </label>
              <input
                type="range"
                min="12"
                max="24"
                step="1"
                value={settings?.fontSize || 16}
                onChange={(e) => update('fontSize', parseInt(e.target.value))}
                style={{ width: '100%', marginTop: '0.4rem' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--color-gray-400)', marginTop: '0.25rem' }}>
                <span>12px (small)</span><span>24px (large)</span>
              </div>
            </div>

            <div className="settings-section-label" style={{ marginTop: '1.5rem' }}>Advanced</div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                className="security-btn"
                onClick={() => {
                  const blob = new Blob([JSON.stringify(settings || {}, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'chatai-settings.json';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <FaDownload /> Export Settings
              </button>
              <button
                className="security-btn danger"
                onClick={() => {
                  if (window.confirm('Reset all settings to defaults?')) update('__RESET__', null);
                }}
              >
                <FaUndo /> Reset to Defaults
              </button>
            </div>
          </div>
        );

      /* â”€â”€ PROFILE â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
      case 'profile':
        return (
          <div className="settings-content">
            <h3 className="content-title">Profile Settings</h3>
            <div className="profile-header">
              <div className="profile-avatar">
                <FaUserCircle />
              </div>
              <div className="profile-info">
                <h4>{profile.name}</h4>
                <p>{profile.dept}</p>
                <span className="user-email">{profile.email}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" value={profile.name} readOnly />
            </div>
            <div className="form-group">
              <label>Employee ID / Department</label>
              <input type="text" value={profile.dept} readOnly />
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" value={profile.email} readOnly />
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-gray-400)', marginTop: '0.75rem' }}>
              Profile information is managed by your Microsoft Azure SSO account.
            </p>
          </div>
        );

      /* â”€â”€ NOTIFICATIONS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
      case 'notifications':
        return (
          <div className="settings-content">
            <h3 className="content-title">Notification Settings</h3>
            <div className="toggle-group">
              <ToggleItem settingKey="soundNotifications" label="Sound Notifications"    description="Play a sound when a new message arrives" />
              <ToggleItem settingKey="notifyNewMessage"   label="Notify on New Message"  description="Show a notification badge for new messages" />
              <ToggleItem settingKey="notifications"      label="Desktop Notifications"  description="Show desktop notifications when app is in the background" />
              <ToggleItem settingKey="emailNotifications" label="Email Notifications"    description="Receive email summaries of daily activities" />
            </div>
          </div>
        );

      /* â”€â”€ APPEARANCE â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
      case 'appearance':
        return (
          <div className="settings-content">
            <h3 className="content-title">Appearance Settings</h3>
            <div className="appearance-options">
              <div className="theme-selector">
                <h4>Theme</h4>
                <div className="theme-options">
                  {[
                    { key: 'light', icon: <FaSun /> },
                    { key: 'dark',  icon: <FaMoon /> },
                    { key: 'auto',  icon: <><FaSun className="sun-icon" /><FaMoon className="moon-icon" /></> },
                  ].map(({ key, icon }) => (
                    <div
                      key={key}
                      className={`theme-option ${theme === key ? 'active' : ''}`}
                      onClick={() => onThemeChange(key)}
                    >
                      <div className={`theme-preview ${key}-theme`}>{icon}</div>
                      <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      /* â”€â”€ PRIVACY & SECURITY â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
      case 'privacy':
        return (
          <div className="settings-content">
            <h3 className="content-title">Privacy &amp; Security</h3>
            <div className="toggle-group">
              <ToggleItem settingKey="endToEndEncryption" label="End-to-End Encryption" description="Encrypt all messages and documents" />
            </div>
            <div className="security-actions" style={{ marginTop: '1.5rem' }}>
              <button
                className="security-btn danger"
                onClick={() => {
                  if (window.confirm('Clear all local ChatAI data? This cannot be undone.')) {
                    localStorage.removeItem('chatbot_settings');
                    localStorage.removeItem('theme');
                    alert('Local data cleared. The page will now refresh.');
                    window.location.reload();
                  }
                }}
              >
                <FaTrash /> Clear Local Data
              </button>
            </div>
          </div>
        );

      /* â”€â”€ ABOUT â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
      case 'about':
        return (
          <div className="settings-content">
            <h3 className="content-title">About NIMI</h3>
            <div className="about-info">
              <div className="app-logo"><FaRobot /></div>
              <h4>NIMI &mdash; NLNG Integrated Machine Intelligence</h4>
              <p className="app-version">Version 1.0.0</p>
              <div className="about-details">
                <p style={{ lineHeight: '1.8', marginBottom: '1rem' }}>
                  <strong>NIMI</strong> (NLNG Integrated Machine Intelligence) is NLNG&rsquo;s intelligent productivity assistant designed to transform the way we work by making information, insights, and everyday tasks more accessible, intuitive, and efficient. Built to support NLNG&rsquo;s digital evolution, NIMI integrates advanced artificial intelligence capabilities to empower employees with instant answers, guided support, and smarter decision&#8209;making tools&mdash;anytime and anywhere.
                </p>
                <p style={{ lineHeight: '1.8', marginBottom: '1rem' }}>
                  At its core, NIMI serves as a unified digital companion that enhances workplace productivity. It streamlines routine activities, provides contextual assistance, and connects employees with the resources they need to work faster and more effectively. From simplifying processes to delivering relevant organizational knowledge, NIMI is engineered to support teams across all functions and levels, helping everyone stay informed, engaged, and empowered.
                </p>
                <p style={{ lineHeight: '1.8' }}>
                  NIMI represents NLNG&rsquo;s commitment to innovation, operational excellence, and a future&#8209;ready workforce. As the platform continues to evolve, it will incorporate richer features, broader integrations, and smarter capabilities&mdash;reflecting our vision of a digitally enabled organization. With NIMI, NLNG is not just adopting technology; we are redefining how we collaborate, solve problems, and deliver value across the business.
                </p>
                <div className="contact-info" style={{ marginTop: '1.5rem' }}>
                  <h5>Contact &amp; Support</h5>
                  <p><FaTicketAlt /> Raise a Ticket on <a href="https://nlng.4me.com/self-service/requests/new/provide_description?from=wizard&service_id=55763&service_instance_id=352900&template_id=160942&subject=Swift%20Vetter%20Support%20Request" target="_blank" rel="noopener noreferrer">4me</a></p>
                  
                </div>
                <div className="legal-info">
                  <h5>Legal</h5>
                  <p>&copy; 2025 Nigeria LNG Limited. All rights reserved.</p>
                </div>
              </div>
            </div>
          </div>
        );


      default:
        return null;
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-sidebar">
        <div className="sidebar-header">
          <button className="back-button" onClick={onClose}>
            <FaArrowLeft />
            <span>Back to App</span>
          </button>
          <h2 className="settings-title">Settings</h2>
        </div>

        <div className="sidebar-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="app-version">v2.1.0</div>
          <div className="copyright">© 2024 NLNG ChatAI</div>
        </div>
      </div>

      <div className="settings-main">
        {renderContent()}
      </div>
    </div>
  );
};

export default Settings;
